import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { venues, users } from '../services/api';
import {
  UserIcon,
  BuildingOfficeIcon,
  KeyIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'engineer' | 'owner' | 'manager' | 'viewer';
  venue_id?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  company_name?: string;
  phone?: string;
}

interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  owner_id?: string;
  created_at: string;
}

interface UserVenueAccess {
  user_id: string;
  venue_id: string;
  access_level: 'owner' | 'manager' | 'viewer';
  granted_by?: string;
  granted_at: string;
  expires_at?: string;
}

type TabType = 'users' | 'venues' | 'assignments';

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { addNotification } = useNotification();
  
  // State
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [usersList, setUsersList] = useState<User[]>([]);
  const [venuesList, setVenuesList] = useState<Venue[]>([]);
  const [assignments, setAssignments] = useState<UserVenueAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [quickAssignData, setQuickAssignData] = useState({
    user_id: '',
    venue_id: '',
    access_level: 'owner'
  });
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'viewer' as 'admin' | 'engineer' | 'owner' | 'manager' | 'viewer',
    venue_id: '',
    company_name: '',
    phone: '',
    is_active: true,
    auto_generate_password: true,
    send_invitation: true
  });

  // Load venues on component mount (needed for dropdowns)
  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'users':
          await loadUsers();
          break;
        case 'venues':
          await loadVenues();
          break;
        case 'assignments':
          await loadAssignments();
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await users.list();
      setUsersList(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadVenues = async () => {
    try {
      const response = await venues.list();
      setVenuesList(response.data);
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await users.getVenueAssignments();
      setAssignments(response.data);
    } catch (error) {
      console.error('Error loading assignments:', error);
    }
  };

  const handleAddUser = async () => {
    try {
      await users.create(formData);
      addNotification('success', 'User created successfully');
      setShowAddModal(false);
      resetForm();
      loadUsers();
    } catch (error: any) {
      addNotification('error', error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedItem) return;
    
    try {
      await users.update(selectedItem.id, formData);
      addNotification('success', 'User updated successfully');
      setShowEditModal(false);
      resetForm();
      loadUsers();
    } catch (error: any) {
      addNotification('error', error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await users.delete(userId);
      addNotification('success', 'User deleted successfully');
      loadUsers();
    } catch (error: any) {
      addNotification('error', error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAssignVenue = async (userId: string, venueId: string, accessLevel: string) => {
    try {
      await users.assignVenue(userId, {
        venue_id: venueId,
        access_level: accessLevel
      });
      addNotification('success', 'Venue assigned successfully');
      loadAssignments();
    } catch (error: any) {
      addNotification('error', error.response?.data?.error || 'Failed to assign venue');
    }
  };

  const handleRemoveAssignment = async (userId: string, venueId: string) => {
    if (!window.confirm('Remove this venue assignment?')) return;
    
    try {
      await users.removeVenueAccess(userId, venueId);
      addNotification('success', 'Assignment removed');
      loadAssignments();
    } catch (error: any) {
      addNotification('error', error.response?.data?.error || 'Failed to remove assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      full_name: '',
      password: '',
      role: 'viewer',
      venue_id: '',
      company_name: '',
      phone: '',
      is_active: true,
      auto_generate_password: true,
      send_invitation: true
    });
    setSelectedItem(null);
  };

  const openEditModal = (user: User) => {
    setSelectedItem(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      password: '', // Don't populate password
      role: user.role,
      venue_id: user.venue_id || '',
      company_name: user.company_name || '',
      phone: user.phone || '',
      is_active: user.is_active,
      auto_generate_password: false, // Don't auto-generate for edits
      send_invitation: false // Don't send invitation for edits
    });
    setShowEditModal(true);
  };

  const renderUsersTab = () => (
    <div className="space-y-6">
      {/* Add User Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Users</h2>
        {(currentUser?.role === 'engineer' || currentUser?.role === 'admin') && (
          <button
            onClick={() => {
              if (venuesList.length === 0) loadVenues();
              setShowAddModal(true);
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add User
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {usersList.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 dark:bg-gray-900">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.full_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin'
                      ? 'bg-red-100 text-red-800'
                      : user.role === 'engineer'
                      ? 'bg-purple-100 text-purple-800'
                      : user.role === 'owner'
                      ? 'bg-blue-100 text-blue-800'
                      : user.role === 'manager'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {user.company_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString()
                    : 'Never'
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => openEditModal(user)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 mr-3"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  {currentUser?.role === 'engineer' && user.id !== currentUser.id && (
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderVenuesTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Venues</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {venuesList.map((venue) => {
          const owner = usersList.find(u => u.id === venue.owner_id);
          return (
            <div key={venue.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <BuildingOfficeIcon className="h-10 w-10 text-blue-500" />
                <button className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400">
                  <PencilIcon className="h-5 w-5" />
                </button>
              </div>
              <h3 className="font-semibold text-lg mb-2">{venue.name}</h3>
              {venue.city && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {venue.city}, {venue.address}
                </p>
              )}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 dark:text-gray-400">Owner</p>
                <p className="font-medium">
                  {owner ? owner.full_name : 'Unassigned'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAssignmentsTab = () => (
    <div className="space-y-6">
      {/* Quick Assign Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Assign</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
            value={quickAssignData.user_id}
            onChange={(e) => setQuickAssignData({ ...quickAssignData, user_id: e.target.value })}
          >
            <option value="">Select User</option>
            {usersList.filter(u => u.role === 'owner').map(user => (
              <option key={user.id} value={user.id}>
                {user.full_name}
              </option>
            ))}
          </select>
          
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
            value={quickAssignData.venue_id}
            onChange={(e) => setQuickAssignData({ ...quickAssignData, venue_id: e.target.value })}
          >
            <option value="">Select Venue</option>
            {venuesList.map(venue => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
          
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
            value={quickAssignData.access_level}
            onChange={(e) => setQuickAssignData({ ...quickAssignData, access_level: e.target.value })}
          >
            <option value="owner">Owner</option>
            <option value="manager">Manager</option>
            <option value="viewer">Viewer</option>
          </select>
          
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={() => {
              if (quickAssignData.user_id && quickAssignData.venue_id) {
                handleAssignVenue(quickAssignData.user_id, quickAssignData.venue_id, quickAssignData.access_level);
                setQuickAssignData({ user_id: '', venue_id: '', access_level: 'owner' });
              } else {
                addNotification('error', 'Please select both user and venue');
              }
            }}
          >
            Assign
          </button>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold">Current Assignments</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Venue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Access Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Assigned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {usersList
                .filter(u => u.venue_id)
                .map((user) => {
                  const venue = venuesList.find(v => v.id === user.venue_id);
                  return (
                    <tr key={`${user.id}-${user.venue_id}`} className="hover:bg-gray-50 dark:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {user.full_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {venue?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Owner
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRemoveAssignment(user.id, user.venue_id!)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Add/Edit User Modal
  const renderModal = () => {
    const isEdit = showEditModal;
    const show = showAddModal || showEditModal;
    
    if (!show) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-9000 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
          <h2 className="text-xl font-semibold mb-4">
            {isEdit ? 'Edit User' : 'Add New User'}
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
                required
              />
            </div>
            
            {!isEdit && (
              <>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.auto_generate_password}
                      onChange={(e) => setFormData({ ...formData, auto_generate_password: e.target.checked })}
                      className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Auto-generate secure password
                    </span>
                  </label>
                  
                  {!formData.auto_generate_password && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
                        required={!formData.auto_generate_password}
                        minLength={8}
                        placeholder="Minimum 8 characters"
                      />
                    </div>
                  )}
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.send_invitation}
                    onChange={(e) => setFormData({ ...formData, send_invitation: e.target.checked })}
                    className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                    Send invitation email with login credentials
                  </label>
                </div>
                
                {formData.auto_generate_password && formData.send_invitation && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">
                      ℹ️ The user will receive an email with a temporary password and will be required to change it on first login.
                    </p>
                  </div>
                )}
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
              >
                {/* Show all roles for admin, limited roles for others */}
                {currentUser?.role === 'admin' ? (
                  <>
                    <option value="admin">Admin</option>
                    <option value="engineer">Engineer</option>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="viewer">Viewer</option>
                  </>
                ) : currentUser?.role === 'engineer' ? (
                  <>
                    <option value="engineer">Engineer</option>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="viewer">Viewer</option>
                  </>
                ) : (
                  <>
                    <option value="owner">Owner</option>
                    <option value="viewer">Viewer</option>
                  </>
                )}
              </select>
            </div>
            
            {(formData.role === 'owner' || formData.role === 'manager' || formData.role === 'viewer') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Assign to Venue
                </label>
                <select
                  value={formData.venue_id}
                  onChange={(e) => setFormData({ ...formData, venue_id: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
                >
                  <option value="">Select a venue</option>
                  {venuesList.map(venue => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Company Name
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                Active
              </label>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 hover:bg-gray-50 dark:bg-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={isEdit ? handleUpdateUser : handleAddUser}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Only engineers and admins can access user management
  if (currentUser?.role !== 'engineer' && currentUser?.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Access denied. Only engineers and administrators can manage users and venues.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage users, venues, and access permissions
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'users', label: 'Users', icon: UserIcon },
            { id: 'venues', label: 'Venues', icon: BuildingOfficeIcon },
            { id: 'assignments', label: 'Assignments', icon: ShieldCheckIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      ) : (
        <>
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'venues' && renderVenuesTab()}
          {activeTab === 'assignments' && renderAssignmentsTab()}
        </>
      )}

      {/* Modals */}
      {renderModal()}
    </div>
  );
};

export default UserManagement;